import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { PersonaState } from '../components/PersonaShared';
import InsightPanel from '../components/InsightPanel';
import { personaTestApi } from '../services/personaService';
import '../styles/persona.scss';

const findInsightScope = (insights, scopeType, scopeIndex) => {
  const scopes = insights?.scopes || [];
  return scopes.find((scope) => {
    const type = scope?.scopeType || scope?.scope_type;
    const index = scope?.scopeIndex ?? scope?.scope_index ?? null;
    return type === scopeType && index === scopeIndex;
  }) || null;
};

const resultHasScopedInsightEvents = (result, scopeType, scopeIndex) => {
  const scoring = result?.rawResponse?.scoringAnalysis
    || result?.raw_response?.scoring_analysis
    || result?.raw_response?.scoringAnalysis
    || {};
  const events = scoring.analysisEvents || scoring.analysis_events || [];
  if (!events.length) return false;
  if (scopeType === 'flow') return true;
  return events.some((event) => (event?.screenIndex ?? event?.screen_index) === scopeIndex);
};

export default function UITestInsightPage() {
  const navigate = useNavigate();
  const { testId } = useParams();
  const [searchParams] = useSearchParams();
  const [test, setTest] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportActionTarget, setReportActionTarget] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [testData, resultData] = await Promise.all([
        personaTestApi.getTest(testId),
        personaTestApi.listResults(testId),
      ]);
      setTest(testData);
      setResults(Array.isArray(resultData) ? resultData : (resultData?.items || []));
    } catch (err) {
      setError(err.message || '인사이트 보고서를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isFlowResult = test?.scope_type === 'flow' && test?.validation_type !== 'ab';
  const insightScopeType = isFlowResult ? 'flow' : 'screen';
  const rawScopeIndex = searchParams.get('scopeIndex');
  const insightScopeIndex = useMemo(() => {
    if (isFlowResult) return null;
    if (rawScopeIndex === null || rawScopeIndex === '') return 0;
    const parsed = Number(rawScopeIndex);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }, [isFlowResult, rawScopeIndex]);

  const activeInsight = useMemo(
    () => findInsightScope(test?.insights, insightScopeType, insightScopeIndex),
    [insightScopeIndex, insightScopeType, test?.insights],
  );
  const hasInsightData = useMemo(
    () => results.some((result) => resultHasScopedInsightEvents(result, insightScopeType, insightScopeIndex)),
    [insightScopeIndex, insightScopeType, results],
  );

  const handleBack = () => navigate(`/tests/ui/${testId}`);

  const topbar = (
    <header className="insight-report-topbar">
      <div className="insight-report-topbar__inner">
        <button type="button" className="insight-report-back" onClick={handleBack} aria-label="결과로 돌아가기">
          <ChevronLeft size={20} />
        </button>
        <div className="insight-report-topbar__copy">
          <p>Insight Report</p>
          <h1>{test?.name || '인사이트 보고서'}</h1>
        </div>
        <div className="insight-report-topbar__actions" ref={setReportActionTarget} />
      </div>
    </header>
  );

  if (loading) {
    return (
      <main className="insight-report-page">
        {topbar}
        <section className="insight-report-body">
          <PersonaState type="loading" title="불러오는 중" message="인사이트 보고서를 준비하고 있습니다." />
        </section>
      </main>
    );
  }

  if (error || !test) {
    return (
      <main className="insight-report-page">
        {topbar}
        <section className="insight-report-body">
          <PersonaState
            title="보고서를 열 수 없습니다"
            message={error || '테스트 정보를 찾지 못했습니다.'}
          />
        </section>
      </main>
    );
  }

  return (
    <main className="insight-report-page">
      {topbar}
      <section className="insight-report-body">
        <InsightPanel
          variant="report"
          testId={testId}
          scopeType={insightScopeType}
          scopeIndex={insightScopeIndex}
          initialInsight={activeInsight}
          hasInsightData={hasInsightData}
          autoGenerate
          reportActionTarget={reportActionTarget}
          onTestUpdate={(nextTest) => {
            setTest(nextTest);
            if (Array.isArray(nextTest?.results)) {
              setResults(nextTest.results);
            }
          }}
        />
      </section>
    </main>
  );
}
